import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import DossiersTabs from "@/components/DossiersTabs";
import { api, formatEUR, formatEURCompact, formatDate } from "@/lib/api";
import { Warning, Buildings, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

function YearSelect({ value, onChange }) {
  const now = new Date().getFullYear();
  const years = [now, now - 1, now - 2];
  return (
    <div className="segmented" data-testid="year-selector">
      <button data-active={!value} onClick={() => onChange(null)} data-testid="year-all">Tout</button>
      {years.map((y) => (
        <button key={y} data-active={value === y} onClick={() => onChange(y)} data-testid={`year-${y}`}>{y}</button>
      ))}
    </div>
  );
}

export default function DossiersPerdus() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = year ? `?year=${year}` : "";
      const res = await api.get(`/dossiers-perdus${params}`);
      setData(res.data);
    } catch { toast.error("Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [year]);

  return (
    <Layout title="Dossiers perdus" centerTitle>
      <button onClick={() => navigate("/")} className="hidden md:inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-5 font-semibold">
        ← Retour
      </button>
      <div className="hidden md:block mb-5">
        <div className="overline">Archive</div>
        <h1 className="font-display text-4xl font-extrabold text-slate-900">Dossiers perdus</h1>
      </div>

      <DossiersTabs activeKey="perdus" />

      <div className="mb-5"><YearSelect value={year} onChange={setYear} /></div>

      {/* Totaux */}
      {data && (
        <div className="grid grid-cols-2 gap-3 mb-5" data-testid="perdus-totals">
          <div className="card-base p-4">
            <div className="overline">Dossiers perdus</div>
            <div className="metric-value text-3xl text-[color:var(--cf-danger)]">{data.totals.count}</div>
          </div>
          <div className="card-base p-4">
            <div className="overline">CA net perdu</div>
            <div className="metric-value text-xl md:text-2xl text-[color:var(--cf-danger)]">{formatEURCompact(data.totals.ca_net_perdu)}</div>
          </div>
        </div>
      )}

      {/* Motifs agrégés */}
      {data?.motifs?.length > 0 && (
        <div className="card-base p-5 mb-5" data-testid="perdus-motifs">
          <div className="overline mb-3">Motifs agrégés</div>
          <ul className="space-y-2">
            {data.motifs.map((m) => (
              <li key={m.motif} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Warning size={14} color="var(--cf-danger)" weight="fill" />
                  <span className="text-sm font-medium text-slate-800">{m.motif}</span>
                </div>
                <span className="pill pill-red text-xs">{m.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="card-base h-28 animate-pulse" />)}</div>
      ) : !data?.items?.length ? (
        <div className="card-base p-8 text-center text-slate-500" data-testid="perdus-empty">
          Aucun dossier perdu {year ? `en ${year}` : ""}.
        </div>
      ) : (
        <div className="space-y-3" data-testid="perdus-list">
          {data.items.map((d) => (
            <button key={d.id} onClick={() => navigate(`/dossier/${d.id}`)} className="card-base p-4 md:p-5 w-full text-left" data-testid={`perdu-${d.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base font-extrabold text-slate-900 truncate">{d.nom}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{d.client_name}</div>
                  {d.banques?.length > 0 && (
                    <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                      <Buildings size={11} /> {d.banques.join(" / ")}
                    </div>
                  )}
                </div>
                <span className="pill pill-red text-[11px]">Perdu</span>
              </div>
              {d.lost_reason && (
                <div className="mt-2 flex items-start gap-2 bg-[color:var(--cf-danger-soft)] rounded-lg p-2">
                  <XCircle size={14} color="var(--cf-danger)" weight="fill" className="mt-0.5 flex-shrink-0" />
                  <div className="text-[13px] text-slate-700">{d.lost_reason}</div>
                </div>
              )}
              <div className="mt-3 flex items-center justify-between text-[12px] text-slate-500">
                <span>Perdu le {formatDate(d.updated_at)}</span>
                <span className="metric-value text-base text-[color:var(--cf-danger)]">{formatEUR(d.ca_net)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </Layout>
  );
}
